import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, Clock, AlertCircle, Loader2, ListTodo } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const PRIORITE_COLORS = {
  urgente: 'bg-red-100 text-red-800 border-red-200',
  haute: 'bg-orange-100 text-orange-800 border-orange-200',
  normale: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  basse: 'bg-slate-100 text-slate-700 border-slate-200',
};

const TYPE_LABELS = {
  facture: '🧾 Facture',
  courrier: '✉️ Courrier',
  achat: '🛒 Achat',
  deplacement: '🚗 Déplacement',
  intervention: '🚜 Intervention',
  rh: '👥 RH',
  paie: '💰 Paie',
  contrat: '📄 Contrat',
  formation: '🎓 Formation',
  autre: '📌 Autre',
};

const AGENT_BADGES = {
  valentine: { label: '🤖 Valentine', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  rh: { label: '👥 Agent RH', cls: 'bg-pink-100 text-pink-800 border-pink-200' },
  direction: { label: '🎯 Direction', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  commercial: { label: '💼 Commercial', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  manuel: { label: '👤 Manuel', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function MesTachesWidget({ compact = true }) {
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ a_faire: 0, en_cours: 0, fait: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/tasks/mes-taches');
      setTasks(data?.tasks || []);
      setCounts(data?.counts || {});
    } catch (e) {
      // silencieux
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // refresh 1 min
    return () => clearInterval(interval);
  }, []);

  const markDone = async (taskId) => {
    try {
      await api.patch(`/tasks/${taskId}`, { statut: 'fait' });
      toast.success('Tâche marquée comme faite ✅');
      load();
    } catch (e) {
      toast.error("Impossible de mettre à jour");
    }
  };

  if (loading) {
    return (
      <Card data-testid="widget-mes-taches">
        <CardContent className="p-6 flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </CardContent>
      </Card>
    );
  }

  const pendingCount = (counts.a_faire || 0) + (counts.en_cours || 0);

  return (
    <Card data-testid="widget-mes-taches" className="overflow-hidden">
      <CardHeader className="pb-3 bg-gradient-to-r from-emerald-50 to-emerald-50/50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-emerald-600" />
            Mes tâches
            {pendingCount > 0 && (
              <Badge className="bg-emerald-600 text-white">{pendingCount}</Badge>
            )}
          </CardTitle>
          {tasks.length > 0 && (
            <Link to="/mes-taches" className="text-xs text-emerald-700 hover:underline">
              Tout voir →
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-3">
        {tasks.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-400" />
            <p className="text-sm">Aucune tâche en attente 🎉</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.slice(0, compact ? 5 : 50).map((t) => {
              const agent = AGENT_BADGES[t.assigned_by_agent] || AGENT_BADGES.manuel;
              const isOverdue = t.echeance && new Date(t.echeance) < new Date();
              return (
                <div
                  key={t.id}
                  data-testid={`task-${t.id}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                >
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => markDone(t.id)}
                    data-testid={`task-checkbox-${t.id}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{t.titre}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <Badge variant="outline" className={`text-xs ${PRIORITE_COLORS[t.priorite]}`}>
                        {t.priorite}
                      </Badge>
                      <Badge variant="outline" className="text-xs bg-white">
                        {TYPE_LABELS[t.type] || t.type}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${agent.cls}`}>
                        {agent.label}
                      </Badge>
                      {t.echeance && (
                        <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                          {isOverdue ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {new Date(t.echeance).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    {t.description && !compact && (
                      <p className="text-xs text-slate-600 mt-1.5">{t.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {tasks.length > 5 && compact && (
              <Link to="/mes-taches" className="block text-center text-xs text-emerald-700 hover:underline pt-2">
                + {tasks.length - 5} autres tâches
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
